import Card from 'components/card';
import testService from 'ApiService/Teacher/TestService';
import { formatDate } from 'pages/helpers/dateUtils';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';
import SelectClass from '../FailedStudents/components/SelectClass';

const ViewTeacherTests = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [classData, setClassData] = useState({ courseId: '', structureId: '', batchId: '' });
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [disable, setDisable] = useState(true);

  const [createdTests, setCreatedTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [teacherScores, setTeacherScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');


  const steps = [
    { stepNo: 1, name: 'Class Info' },
    { stepNo: 2, name: 'Test Reviews' },
  ];

  const handleNext = () => {
    if (!disable) {
      fetchCreatedTests();
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const clearData = () => {
    setCreatedTests([]);
    setSelectedTest(null);
    setSelectedSubmission(null);
    setFilterType('');
    setFilterDepartment('');
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await testService.getAllCourses();
      if (response.data.success) setCourses(response.data.courses);
      else toast.error('Failed to fetch courses.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCreatedTests = async () => {
    setLoading(true);
    try {
      const response = await testService.getTestsByBatch({
        batchId: classData.batchId,
        structureId: classData.structureId,
      });
      if (response.data.success) {
        setCreatedTests(response.data.data);
      } else {
        toast.error(response.data.message || 'Failed to fetch tests.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch tests.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async (testId) => {
    setLoading(true);
    try {
      const response = await testService.getSubmissionsByTestId(testId);
      if (response.data.success) {
        setSubmissions(response.data.data);
        setSelectedTest(createdTests.find((test) => test._id === testId));
      } else {
        toast.error(response.data.message || 'Failed to fetch submissions.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch submissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (questionId, value) => {
    setTeacherScores((prevScores) => ({
      ...prevScores,
      [questionId]: value,
    }));
  };

  const handleSubmitScores = async () => {
    try {
      const totalScore = selectedSubmission.questions.reduce(
        (sum, question) => {
          const score = parseFloat(teacherScores[question.questionId]) || 0;
          return sum + score;
        },
        0
      );

      const data = {
        submissionId: selectedSubmission.submissionId,
        totalScore,
      };

      const response = await testService.submitQATestScores(data);

      if (response.data.success) {
        toast.success('Scores submitted successfully!');
        setSelectedSubmission(null);
      } else {
        toast.error(response.data.message || 'Failed to submit scores.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to submit scores.');
    }
  };

  const departments = [...new Set(createdTests.map((t) => t.department).filter(Boolean))];

  const filteredTests = createdTests.filter((t) => {
    if (filterType && t.type !== filterType) return false;
    if (filterDepartment && t.department !== filterDepartment) return false;
    return true;
  });

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && currentStep === 1 && !disable) handleNext();
    else if (event.key === 'ArrowLeft') handleBack();
    else if (event.key === 'ArrowRight' && currentStep === 1 && !disable) handleNext();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, disable]);

  const displayStep = (step) => {
    switch (step.stepNo) {
      case 1:
        return (
          <SelectClass
            setDisable={setDisable}
            setClassData={setClassData}
            classData={classData}
            courses={courses}
            loadingCourses={loadingCourses}
            clearData={clearData}
          />
        );
      case 2:
        if (loading && createdTests.length === 0) {
          return (
            <div className="flex h-96 w-full items-center justify-center">
              <BallTriangle color="#5431ed" />
            </div>
          );
        }
        return (
          <div className="w-full px-3 pt-7 md:px-8">
            <div className="mb-4 flex items-center justify-between">
              <button
                onClick={handleBack}
                className="cursor-pointer rounded-xl bg-lightPrimary px-6 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
              >
                ← Back
              </button>
              <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                Test Reviews
              </h4>
              <div />
            </div>

            {selectedSubmission ? (
              <div className="mt-6 grid h-full w-full grid-cols-1 gap-4 rounded-lg border p-2 text-navy-800 dark:text-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold">
                    {selectedSubmission.student?.name || 'Unknown'}&apos;s Submission
                  </h3>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      selectedSubmission.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {selectedSubmission.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </div>
                <p>Test Title: {selectedTest?.title}</p>
                <p>Score: {selectedSubmission.score || 'Not graded yet'}</p>
                {selectedSubmission.questions?.length > 0 ? (
                  selectedSubmission.questions.map((question, index) => (
                    <div
                      key={question.questionId || index}
                      className="rounded-lg bg-white p-4 shadow-md dark:bg-navy-900"
                    >
                      <h5 className="text-lg font-bold text-gray-900 dark:text-white">
                        Question {index + 1}: {question.questionText}
                      </h5>
                      <p className="mt-2 text-green-600 dark:text-green-400">
                        Correct Answer: {question.correctAnswer}
                      </p>
                      <p className="mt-2">
                        Student Answer:{' '}
                        <span
                          className={
                            question.isCorrect
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }
                        >
                          {question.studentAnswer || 'Not answered'}
                        </span>
                      </p>
                      {selectedSubmission.type === 'qa' && (
                        <div className="mt-4">
                          {selectedSubmission.score === 0 ? (
                            <>
                              <label className="block text-sm font-medium text-gray-700">
                                Teacher Score:
                              </label>
                              <input
                                type="number"
                                value={teacherScores[question.questionId] || ''}
                                onChange={(e) =>
                                  handleScoreChange(question.questionId, e.target.value)
                                }
                                className="block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-navy-800 dark:text-gray-300 sm:text-sm"
                              />
                            </>
                          ) : (
                            <p className="text-gray-700 dark:text-gray-300">
                              Score: {teacherScores[question.questionId] || question.marks}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">No questions found in this submission.</p>
                )}
                {selectedSubmission.type === 'qa' && selectedSubmission.score === 0 && (
                  <button
                    onClick={handleSubmitScores}
                    className="linear mt-4 cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300"
                  >
                    Submit Scores
                  </button>
                )}
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="mt-4 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white"
                >
                  Back to Submissions
                </button>
              </div>
            ) : selectedTest ? (
              <div className="mt-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                    {selectedTest.title} — Submissions
                  </h3>
                  <button
                    onClick={() => setSelectedTest(null)}
                    className="cursor-pointer rounded-xl bg-lightPrimary px-8 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white"
                  >
                    Back to Tests
                  </button>
                </div>
                {submissions.length === 0 ? (
                  <div className="flex h-40 items-center justify-center rounded-lg border border-gray-200 p-6 dark:border-white/10">
                    <p className="text-lg text-gray-500">
                      No students have submitted this test yet.
                    </p>
                  </div>
                ) : (
                  <div className="grid h-full max-h-[60vh] grid-cols-1 gap-4 overflow-y-scroll md:grid-cols-2 lg:grid-cols-3">
                    {submissions.map((submission) => (
                      <span
                        onClick={() => setSelectedSubmission(submission)}
                        key={submission.submissionId}
                        className="cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none hover:border-brand-500 dark:border-[#323B5D] dark:bg-navy-900 dark:text-white"
                      >
                        <div className="flex h-full w-full flex-col p-4 leading-normal md:w-2/3">
                          <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                            {submission.student?.name || 'Unknown'}
                          </h5>
                          <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                            Score: {submission.score || 'Not graded'}
                          </p>
                          <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                            Submitted:{' '}
                            {submission.submittedAt
                              ? new Date(submission.submittedAt).toLocaleDateString()
                              : 'N/A'}
                          </p>
                          <span
                            className={`inline-block w-fit rounded-full px-3 py-1 text-xs font-medium ${
                              submission.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {submission.status === 'completed'
                              ? 'Completed'
                              : 'In Progress'}
                          </span>
                        </div>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Filters Row */}
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-bold text-navy-700 dark:text-white whitespace-nowrap">
                      Type:
                    </label>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-32 cursor-pointer rounded-xl border border-gray-200 py-2 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    >
                      <option value="">All</option>
                      <option value="mcq">MCQ</option>
                      <option value="qa">QA</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-sm font-bold text-navy-700 dark:text-white whitespace-nowrap">
                      Department:
                    </label>
                    <select
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      className="w-40 cursor-pointer rounded-xl border border-gray-200 py-2 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    >
                      <option value="">All</option>
                      {departments.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 grid h-full max-h-[55vh] w-full grid-cols-1 gap-4 overflow-y-scroll rounded-xl border border-gray-200 p-2 dark:border-white/10 md:grid-cols-2 lg:grid-cols-3">
                  {filteredTests.length === 0 ? (
                    <div className="col-span-full flex h-40 items-center justify-center">
                      <p className="text-lg text-gray-500">No tests found.</p>
                    </div>
                  ) : (
                    filteredTests.map((test, i) => (
                      <span
                        onClick={() => fetchSubmissions(test._id)}
                        key={test._id}
                        className="cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none hover:border-brand-500 dark:border-[#323B5D] dark:bg-navy-900 dark:text-white"
                      >
                        <div className="flex h-full w-full flex-col p-4 leading-normal md:w-2/3">
                          <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                            {i + 1}. {test.title}
                          </h5>
                          <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                            Type: {test.type?.toUpperCase() || 'N/A'}
                          </p>
                          <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                            Course: {test.batchStructure?.courses?.map((c) => c.courseName).filter(Boolean).join(', ') || 'N/A'}
                          </p>
                          <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                            Batch:{' '}
                            {test.assignedTo?.length
                              ? test.assignedTo
                                  .map(
                                    (batch) =>
                                      `Batch ${batch.batchNumber} (${new Date(
                                        batch.startDate
                                      ).toLocaleDateString()} to ${new Date(
                                        batch.endDate
                                      ).toLocaleDateString()})`
                                  )
                                  .join(', ')
                              : test.assignedStudents?.length
                              ? test.assignedStudents.map((s) => s.name).join(', ')
                              : 'N/A'}
                          </p>
                          <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                            Created: {formatDate(test.createdAt)}
                          </p>
                          <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
                            Scheduled: {formatDate(test.scheduledAt) || 'N/A'}
                          </p>
                        </div>
                      </span>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (currentStep === 1) {
    return (
      <div className="mt-3 h-full w-full">
        <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
        <div className="w-md:2/3 mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
          <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[70px] h-full mx-auto pb-3">
            <div className="w-full rounded-[20px]">
              <UseContextProvider>
                {displayStep(steps[0])}
              </UseContextProvider>
            </div>
            <div className="flex justify-end px-3 pt-2 md:px-8">
              <button
                disabled={disable}
                onClick={handleNext}
                className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
              >
                {loadingCourses ? 'Loading...' : 'Next'}
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full p-4">
      <UseContextProvider>
        {displayStep(steps[1])}
      </UseContextProvider>
    </div>
  );
};

export default ViewTeacherTests;
